import store from '@/state/store';

const routes = [
    { name: 'academic-courses', permission: 'academic_courses_management' },
    { name: 'ads', permission: 'ads_management' },
    { name: 'articles', permission: 'articles_management' },
    { name: 'article-categories', permission: 'articles_management' },
    { name: 'article-tags', permission: 'articles_management' },
    { name: 'attributes', permission: 'shops_management' },
    { name: 'boards', permission: 'boards' },
    'campuses',
    { name: 'courses', permission: 'marathons_management' },
    { name: 'disclaimers', permission: 'shops_management' },
    'events',
    'forms',
    { name: 'homepages', permission: 'contents' },
    { name: 'footer-links', permission: 'contents' },
    { name: 'loan-shops', permission: 'shops_management' },
    'notifications',
    { name: 'orders', permission: 'shops_management' },
    { name: 'pages', permission: 'contents' },
    { name: 'posts', permission: 'boards' },
    { name: 'product-tags', permission: 'shops_management' },
    { name: 'shops', permission: 'shops_management' },
    { name: 'suppliers', permission: 'shops_management' },
    { name: 'students', permission: 'students_management' },
    { name: 'student-tags', permission: 'students_management' },
    { name: 'student-tags-list', permission: 'students_management' },
    { name: 'student-tag-categories', permission: 'students_management' },
    { name: 'star-products', permission: 'stars_products_management' },
    { name: 'teachers', permission: 'marathons_management' },
    { name: 'transparency', permission: 'contents' },
    { name: 'tiers', permission: 'stars_products_management' },
    { name: 'users', permission: 'users' },
    { name: 'hugs', permission: 'users' }
];

const defaultOptions = {
    index: true,
    add: true,
    edit: true
};

const crudRoutes = routes.flatMap(route => {
    const property = route.name || route;

    const meta = {
        authRequired: true
    };

    if (route.permission) {
        meta.permission = route.permission;
    }

    const indexRoute = {
        path: `/${property}`,
        name: property,
        component: () => import(`@/views/${property}/Table`),
        meta
    };
    const addRoute = {
        path: `/${property}/add`,
        name: `${property}Add`,
        component: () => import(`@/views/${property}/AddEdit`),
        meta
    };
    const editRoute = {
        path: `/${property}/:id/edit`,
        name: `${property}Edit`,
        component: () => import(`@/views/${property}/AddEdit`),
        meta
    };

    if (typeof route === 'string') {
        return [indexRoute, addRoute, editRoute];
    }

    const { index, add, edit } = { ...defaultOptions, ...route };
    const routes = [];

    if (index) {
        routes.push(indexRoute);
    }

    if (add) {
        routes.push(addRoute);
    }

    if (edit) {
        routes.push(editRoute);
    }

    return routes;
});

export default [
    ...crudRoutes,

    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/auth/Login'),
        meta: {
            guestOnly: true,
            layout: 'auth'
        }
    },
    {
        path: '/logout',
        name: 'logout',
        meta: {
            authRequired: true,
            beforeResolve() {
                store.dispatch('auth/logout');
            }
        }
    },

    {
        path: '/',
        name: 'dashboard',
        component: () => import('@/views/dashboard/Dashboard'),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/account',
        name: 'account',
        component: () => import('@/views/account/Account'),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/boards/:boardId/posts/add',
        name: 'postAdd',
        component: () => import('@/components/posts/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'boards'
        }
    },
    {
        path: '/student-tags-list/:id/:tagId/edit',
        name: 'studentTagEdit',
        component: () => import('@/views/student-tags-list/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'students_management'
        }
    },
    {
        path: '/boards/:boardId/posts/:postId/edit',
        name: 'postEdit',
        component: () => import('@/components/posts/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'boards'
        }
    },
    {
        path: '/comments',
        name: 'commentsTable',
        component: () => import('@/views/comments/Table'),
        meta: {
            authRequired: true,
            permission: 'academic_courses_management'
        }
    },
    {
        path: '/comments/:id/edit',
        name: 'commentEdit',
        component: () => import('@/views/comments/Edit'),
        meta: {
            authRequired: true,
            permission: 'academic_courses_management'
        }
    },
    {
        path: '/contents',
        name: 'contentsTable',
        component: () => import('@/views/contents/Table'),
        meta: {
            authRequired: true,
            permission: 'contents'
        }
    },
    {
        path: '/contents/:key',
        name: 'singleContent',
        component: () => import('@/views/contents/Single'),
        meta: {
            authRequired: true,
            permission: 'contents'
        }
    },
    {
        path: '/files',
        name: 'filesTable',
        component: () => import('@/views/files/Table'),
        meta: {
            authRequired: true,
            permission: 'academic_courses_management'
        }
    },
    {
        path: '/files/:id/edit',
        name: 'fileEdit',
        component: () => import('@/views/files/Edit'),
        meta: {
            authRequired: true,
            permission: 'academic_courses_management'
        }
    },
    {
        path: '/footer-columns',
        name: 'footerColumns',
        component: () => import('@/views/footer-columns/Table'),
        meta: {
            authRequired: true,
            permission: 'contents'
        }
    },
    {
        path: '/footer-columns/:id/edit',
        name: 'footerColumnsEdit',
        component: () => import('@/views/footer-columns/Edit'),
        meta: {
            authRequired: true,
            permission: 'contents'
        }
    },
    {
        path: '/homepage-components',
        name: 'homepageComponentsTable',
        component: () => import('@/views/homepage-components/Table'),
        meta: {
            authRequired: true,
            permission: 'contents'
        }
    },
    {
        path: '/homepage-components/:type/add',
        name: 'homepageComponentAdd',
        component: () => import('@/views/homepage-components/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'contents'
        }
    },
    {
        path: '/homepage-components/:type/:id/edit',
        name: 'homepageComponentEdit',
        component: () => import('@/views/homepage-components/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'contents'
        }
    },
    {
        path: '/loans',
        name: 'loan',
        component: () => import('@/views/loans/Table'),
        meta: {
            authRequired: true,
            permission: 'shops_management'
        }
    },
    {
        path: '/loan-shops/:id/loan-products',
        name: 'loanShopProducts',
        component: () => import('@/views/loan-shops/Products'),
        meta: {
            authRequired: true,
            permission: 'shops_management'
        }
    },
    {
        path: '/loan-shops/:loanShopId/loan-products/add',
        name: 'loanProductAdd',
        component: () => import('@/views/loan-products/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'shops_management'
        }
    },
    {
        path: '/loan-shops/:loanShopId/loan-products/:id/edit',
        name: 'loanProductEdit',
        component: () => import('@/views/loan-products/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'shops_management'
        }
    },
    {
        path: '/media',
        name: 'media',
        component: () => import('@/views/media/Management'),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/media/:id/crop',
        name: 'cropMedia',
        component: () => import('@/views/media/Crop'),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/posts',
        name: 'postsTable',
        component: () => import('@/views/posts/Table'),
        meta: {
            authRequired: true,
            permission: 'boards'
        }
    },
    {
        path: '/reported-content',
        name: 'reportedContent',
        component: () => import('@/views/reported-content/Table'),
        meta: {
            authRequired: true,
            permission: 'academic_courses_management'
        }
    },
    {
        path: '/shops/:id/products',
        name: 'shopProducts',
        component: () => import('@/views/shops/Products'),
        meta: {
            authRequired: true,
            permission: 'shops_management'
        }
    },
    {
        path: '/shops/:shopId/products/add',
        name: 'productAdd',
        component: () => import('@/views/products/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'shops_management'
        }
    },
    {
        path: '/shops/:shopId/products/:id/edit',
        name: 'productEdit',
        component: () => import('@/views/products/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'shops_management'
        }
    },
    {
        path: '/shops/:shopId/products/:productId/variants/add',
        name: 'productVariantAdd',
        component: () => import('@/views/product-variants/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'shops_management'
        }
    },
    {
        path: '/shops/:shopId/products/:productId/variants/:id/edit',
        name: 'productVariantEdit',
        component: () => import('@/views/product-variants/AddEdit'),
        meta: {
            authRequired: true,
            permission: 'shops_management'
        }
    },
    {
        path: '/requests',
        name: 'requests',
        component: () => import('@/views/requests/Table'),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/requests/:id/edit',
        name: 'requestsEdit',
        component: () => import('@/views/requests/Edit'),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/settings',
        name: 'settings',
        component: () => import('@/views/settings/Settings'),
        meta: {
            authRequired: true,
            permission: 'settings'
        }
    },
    {
        path: '/teachers/:id/courses',
        name: 'teacherCourses',
        component: () => import('@/views/teachers/Courses'),
        meta: {
            authRequired: true,
            permission: 'marathons_management'
        }
    },
    {
        path: '/terms-of-use',
        name: 'termsOfUse',
        component: () => import('@/views/terms-of-use/Table'),
        meta: {
            authRequired: true,
            permission: 'contents'
        }
    },
    {
        path: '/terms-of-use/:id/edit',
        name: 'termsOfUseEdit',
        component: () => import('@/views/terms-of-use/Edit'),
        meta: {
            authRequired: true,
            permission: 'contents'
        }
    },
    {
        path: '/events/:eventId/tickets/:code/accept',
        name: 'ticketAccept',
        component: () => import('@/views/events/Accept'),
        meta: {
            authRequired: true
        }
    },
    {
        path: '/courses/:id/users',
        name: 'courseUsers',
        component: () => import('@/views/courses/Users'),
        meta: {
            authRequired: true,
            permission: 'marathons_management'
        }
    },
    {
        path: '/course-orders',
        name: 'courseOrders',
        component: () => import('@/views/course-orders/Table'),
        meta: {
            authRequired: true,
            permission: 'marathons_management'
        }
    },
    {
        path: '/ad-positions',
        name: 'adPositions',
        component: () => import('@/views/ad-positions/Table'),
        meta: {
            authRequired: true,
            permission: 'ads_management'
        }
    },
    {
        path: '/ad-positions/:id/ads',
        name: 'adPositionAds',
        component: () => import('@/views/ad-positions/Ads'),
        meta: {
            authRequired: true
        }
    },

    {
        path: '*',
        name: 'notFoundPage',
        component: () => import('@/layouts/error'),
        meta: {
            layout: (() =>
                store.getters['auth/loggedIn'] ? 'default' : 'auth')()
        }
    }
];
